import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { registrarErro, tratarErro } from '@/lib/erros';
import {
  maiorData,
  montarRecebimento,
  somarRecebimentos,
  ultimoRecebimentoDe,
} from '@/leads/recebimentos';
import { chaveMesDe, mensagemMesFechado } from '@/remuneracao/calculo';

/**
 * Recebimentos do paciente (VA) — o que ele efetivamente pagou, ao longo dos
 * meses. É a base da comissão futura, então cada escrita mexe no documento do
 * recebimento E nos derivados do lead, sempre no MESMO lote: um número de
 * comissão que discorda da lista que o gerou seria pior que não ter o número.
 *
 * Escrita só do GESTOR (rules): quem lança não é quem ganha comissão. O SDR lê.
 */

const colecao = (clinicaId, leadId) =>
  collection(db, 'clinicas', clinicaId, 'leads', leadId, 'recebimentos');
const refRecebimento = (clinicaId, leadId, id) =>
  doc(db, 'clinicas', clinicaId, 'leads', leadId, 'recebimentos', id);
const refLead = (clinicaId, leadId) => doc(db, 'clinicas', clinicaId, 'leads', leadId);

/**
 * ★★ Travas do fechamento (03/11/2026, item 1) — recebimento de mês FECHADO é recusado.
 *
 * As rules são a autoridade (`mesFechado` em `firestore.rules`); isto existe para a mensagem
 * dizer O PORQUÊ, em vez de um "permissão negada" que ninguém entende. ⚠ Se o mês for fechado
 * entre esta conferência e a gravação, as rules recusam — e o `catch` abaixo devolve a mesma
 * frase.
 */
async function exigirMesAberto(clinicaId, mes) {
  if (!mes) return;
  const snap = await getDoc(doc(db, 'clinicas', clinicaId, 'fechamentos', mes));
  if (snap.exists() && snap.data()?.status === 'fechado') {
    throw new Error(mensagemMesFechado({ mes }));
  }
}

const mesDe = (recebimento) => recebimento?.mes || chaveMesDe(recebimento?.data);

async function gravarConferindoMes(clinicaId, mes, lote) {
  await exigirMesAberto(clinicaId, mes);
  try {
    await lote.commit();
  } catch (e) {
    if (e?.code === 'permission-denied') {
      const snap = await getDoc(doc(db, 'clinicas', clinicaId, 'fechamentos', mes)).catch((erroLeitura) => {
        // ⚠ Não é catch mudo (WT 1b): registra, e o erro ORIGINAL da gravação sobe logo abaixo.
        registrarErro({
          origem: 'service',
          codigo: erroLeitura?.code ?? 'sem-codigo',
          mensagem: erroLeitura?.message ?? String(erroLeitura),
          assunto: 'o fechamento do mês do recebimento',
        });
        return null;
      });
      if (snap?.exists() && snap.data()?.status === 'fechado') throw new Error(mensagemMesFechado({ mes }));
    }
    throw e;
  }
}

/** Recebimentos do lead, do mais recente para o mais antigo. */
export function ouvirRecebimentos(clinicaId, leadId, aoReceber, aoFalhar) {
  const q = query(colecao(clinicaId, leadId), orderBy('data', 'desc'));
  return onSnapshot(
    q,
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('os recebimentos', aoFalhar),
  );
}

/**
 * Lança um recebimento e atualiza os derivados do lead no mesmo lote.
 *
 * `valorRecebido` por `increment`: lançar é o caminho quente (dois gestores no
 * mesmo lead no fechamento do mês) e o incremento é imune à corrida — somar uma
 * lista lida antes perderia o lançamento do outro.
 *
 * `ultimoRecebimentoEm` NÃO retrocede: lançar hoje uma parcela de março não pode
 * fazer o lead parecer "parado desde março". Por isso `maiorData`.
 */
export async function registrarRecebimento(clinicaId, lead, dados, autor) {
  const recebimento = montarRecebimento(
    { ...dados, clinicaId, leadId: lead.id },
    autor,
    { agora: serverTimestamp() },
  );

  const lote = writeBatch(db);
  lote.set(doc(colecao(clinicaId, lead.id)), recebimento);
  lote.update(refLead(clinicaId, lead.id), {
    valorRecebido: increment(recebimento.valor),
    ultimoRecebimentoEm: maiorData(lead.ultimoRecebimentoEm, recebimento.data),
    atualizadoEm: serverTimestamp(),
  });
  await gravarConferindoMes(clinicaId, recebimento.mes, lote);
}

/**
 * Remove um recebimento e RECALCULA os derivados a partir do que sobrou.
 *
 * Recalcular, e não `increment(-valor)`: o carimbo do último recebimento precisa
 * da lista de qualquer forma, e somar de novo CONSERTA um `valorRecebido` que
 * tenha divergido, em vez de perpetuar o erro. A lista vem da tela, que já a
 * assina — nenhuma leitura extra.
 */
export async function removerRecebimento(clinicaId, lead, recebimento, recebimentosAtuais = []) {
  const restantes = (recebimentosAtuais ?? []).filter((r) => r.id !== recebimento.id);

  const lote = writeBatch(db);
  lote.delete(refRecebimento(clinicaId, lead.id, recebimento.id));
  lote.update(refLead(clinicaId, lead.id), {
    valorRecebido: somarRecebimentos(restantes),
    ultimoRecebimentoEm: ultimoRecebimentoDe(restantes),
    atualizadoEm: serverTimestamp(),
  });
  await gravarConferindoMes(clinicaId, mesDe(recebimento), lote);
}

/** Só para limpeza em teste; a UI não oferece apagar sem recalcular. */
export const apagarRecebimentoSemRecalcular = (clinicaId, leadId, id) =>
  deleteDoc(refRecebimento(clinicaId, leadId, id));
